/**
 * Centralised share/tracking link generator.
 *
 * Every share/tracking URL across the app funnels through `getShareLink` so we
 * can flip the base host in one place if we ever move to a dedicated tracking
 * subdomain (e.g. `t.ihsanone.org`). Today it resolves to `window.location.origin`,
 * which means preview and production auto-adapt with zero code changes.
 *
 * You can also override at build time via REACT_APP_LINK_BASE if needed:
 *   REACT_APP_LINK_BASE=https://t.ihsanone.org yarn build
 */

const getLinkBase = () => {
  if (process.env.REACT_APP_LINK_BASE) return process.env.REACT_APP_LINK_BASE;
  if (typeof window !== 'undefined' && window.location) return window.location.origin;
  return '';
};

/**
 * Today's date as DDMM (e.g. "22Apr" → "2204"). Used by tracker link IDs.
 */
export const todayDDMM = () => {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}${mm}`;
};

/**
 * Random short slug for tracker IDs (12 chars, base36).
 */
export const shortId = () => Math.random().toString(36).slice(2, 14);

/**
 * Build any share/tracking URL used by the app.
 *
 * kinds:
 *  - 'casket-track'    : { ddmm, pkg, id }    → /{ddmm}/{pkg}/{id}/v
 *  - 'casket-past'     : { slug }             → /past/{slug}/v
 *  - 'casket-attended' : { slug }             → /attended/{slug}/completed
 *  - 'assist-track'    : { ddmm, id }         → /{ddmm}/assist/{id}/v
 *  - 'referral'        : { slug }             → /r/{slug}
 */
export const getShareLink = (kind, params = {}) => {
  const base = getLinkBase();
  switch (kind) {
    case 'casket-track':
      return `${base}/${params.ddmm}/${params.pkg}/${params.id}/v`;
    case 'casket-past':
      return `${base}/past/${params.slug}/v`;
    case 'casket-attended':
      return `${base}/attended/${params.slug}/completed`;
    case 'assist-track':
      return `${base}/${params.ddmm}/assist/${params.id}/v`;
    case 'referral':
      return `${base}/r/${params.slug}`;
    default:
      return base;
  }
};
